import type React from "react";
import styled from "@emotion/styled";
import { memo } from "react";
import { text, surface, brand, interactive, shadow, zIndex, borderRadius, spacing, dataViz } from "@atlas-int/tokens";
import type { ScaleLinear } from "d3-scale";
import type { ScatterplotDatum, FilteredDataLookup } from "./scatterplotTypes";
import type { GrowthPY } from "./scatterplotTypes";
import { Button } from "@atlas-int/ui";

const OptionButton = styled(Button)<{ $active?: boolean }>`
  position: absolute;
  border: 1px solid ${text.primary};
  margin: ${spacing[0]};
  height: 34px;
  width: 140px;
  letter-spacing: 0.2px;
  transform-origin: left center;
  transform: rotate(270deg) translateX(-50px);
  border-radius: 0;
  background-color: ${(props) => (props.$active ? brand.dark : surface.white)};
  color: ${(props) => (props.$active ? text.onDark : text.primary)};

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.$active ? brand.dark : surface.hover)};
    color: ${(props) => (props.$active ? text.onDark : text.primary)};
  }
`;
const TopButton = styled(OptionButton)`
  border-bottom-right-radius: ${borderRadius.base};
  border-top-right-radius: ${borderRadius.base};
  border-left: 0px;
  top: 80px;
`;

const BottomButton = styled(OptionButton)`
  border-bottom-left-radius: ${borderRadius.base};
  border-top-left-radius: ${borderRadius.base};
  border-right: 0px;
  top: 220px;
`;

const yAxisOptionsContainerStyle: React.CSSProperties = {
  position: "relative",
  height: "300px",
  margin: "0 20px",
};

export const YAxisOptions = ({ yAxis, onYAxisChange }: { yAxis?: string; onYAxisChange?: (yAxis: string) => void }) => {
  return (
    <div style={yAxisOptionsContainerStyle} data-download-exclude>
      <TopButton
        variant="ghost"
        size="sm"
        $active={yAxis === "cog"}
        onClick={() => onYAxisChange?.("cog")}
      >
        Opportunity Gain
      </TopButton>
      <BottomButton
        variant="ghost"
        size="sm"
        $active={yAxis === "pci"}
        onClick={() => onYAxisChange?.("pci")}
      >
        Product Complexity
      </BottomButton>
    </div>
  );
};

const YAxisLable = styled.div`
  transform: rotate(270deg);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  font-size: 1em;
  height: 100%;
  width: 20px;
  padding: ${spacing[0]};
  display: flex;
  align-items: center;
`;

const yAxisLabelsContainerStyle: React.CSSProperties = {
  transform: "translate(10px)",
  paddingLeft: spacing[2],
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
};

const topLabelStyle: React.CSSProperties = {
  position: "absolute",
  bottom: "20%",
  transform: "rotate(270deg) translateX(-50%)",
  gap: spacing[2],
};

const bottomLabelStyle: React.CSSProperties = {
  position: "absolute",
  top: "30%",
  transform: "rotate(270deg) translateX(50%)",
  gap: spacing[2],
};

const chevronStyle: React.CSSProperties = { display: "inline-block", transform: "rotate(0deg)" };
const chevronReverseStyle: React.CSSProperties = { display: "inline-block", transform: "rotate(180deg)" };

export const YAxisLabels = ({ topLabel, bottomLabel }: { topLabel: React.ReactNode; bottomLabel: React.ReactNode }) => (
  <div style={yAxisLabelsContainerStyle}>
    <YAxisLable style={topLabelStyle}>
      {topLabel}{" "}
      <span style={chevronStyle}>
        ▶
      </span>
    </YAxisLable>
    <YAxisLable style={bottomLabelStyle}>
      <span style={chevronReverseStyle}>
        ▶
      </span>{" "}
      {bottomLabel}
    </YAxisLable>
  </div>
);

const XAxisLable = styled.div`
  pointer-events: none;
  user-select: none;
  font-size: 1em;
`;

const xAxisContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const xAxisLabelsRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  width: 300,
};

const xAxisLabelItemStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: spacing[2] };

const distanceLabelStyle: React.CSSProperties = { fontWeight: "semiBold", fontSize: "22px", color: dataViz.primaryAction };

export const XAxisLabels = () => (
  <div style={xAxisContainerStyle}>
    <div style={xAxisLabelsRowStyle}>
      <XAxisLable style={xAxisLabelItemStyle}>
        ◀ More Nearby
      </XAxisLable>
      <XAxisLable style={xAxisLabelItemStyle}>
        Less Nearby ▶
      </XAxisLable>
    </div>
    <div style={distanceLabelStyle}>
      Distance
    </div>
  </div>
);

export const AxisAnnotation = styled.div`
  position: absolute;
  background-color: ${dataViz.primaryAction};
  border: 1px solid ${interactive.muted};
  border-radius: ${borderRadius.base};
  padding: ${spacing[2]} ${spacing[4]};
  font-size: 0.9rem;
  pointer-events: none;
  z-index: ${zIndex.tooltip};
  box-shadow: ${shadow.sm};
  white-space: nowrap;
  color: white;
`;

interface AxisAnnotationsProps {
  activeItem: string | null;
  filteredDataLookup: FilteredDataLookup;
  ySelection: string;
  totalExportLookup: Map<string, GrowthPY>;
  width: number;
  height: number;
  yPadding: number;
  zoomedXScale: ScaleLinear<number, number>;
  zoomedYScale: ScaleLinear<number, number>;
}

export const AxisAnnotations = memo(
  ({
    activeItem,
    filteredDataLookup,
    ySelection,
    totalExportLookup,
    width: _width,
    height,
    yPadding,
    zoomedXScale,
    zoomedYScale,
  }: AxisAnnotationsProps) => {
    if (!activeItem || !filteredDataLookup.get(activeItem)) return null;

    const item = filteredDataLookup.get(activeItem);

    // Extract the actual data values for scaling
    const distanceValue = item.distance;
    const yValueRaw =
      ySelection === "cog" ? item.cog : totalExportLookup.get(activeItem).pci;

    // Format for display
    const yValue = yValueRaw.toFixed(3);
    const xValue = distanceValue.toFixed(3);

    // Get the positions using the zoomed scales
    const xPosition = zoomedXScale(distanceValue);
    const yPosition = zoomedYScale(yValueRaw);

    return (
      <>
        {/* Y-axis value annotation (left side) */}
        <AxisAnnotation
          style={{
            left: "5px",
            top: `${yPosition - 12}px`,
          }}
        >
          {yValue}
        </AxisAnnotation>

        {/* X-axis value annotation (bottom) */}
        <AxisAnnotation
          style={{
            left: `${xPosition}px`,
            top: `${height - yPadding / 2 + 5}px`,
            transform: "translateX(-50%)",
          }}
        >
          {xValue}
        </AxisAnnotation>
      </>
    );
  },
);

interface HighlightLinesProps {
  activeItem: string | null;
  filteredDataLookup: FilteredDataLookup;
  width: number;
  height: number;
  yPadding: number;
  zoom: { transformMatrix: { scaleX: number; scaleY: number; translateX: number; translateY: number } };
  zoomedXScale: ScaleLinear<number, number>;
  zoomedYScale: ScaleLinear<number, number>;
  ySelection: string;
  totalExportLookup: Map<string, GrowthPY>;
}

export const HighlightLines = memo(
  ({
    activeItem,
    filteredDataLookup,
    width,
    height,
    yPadding: _yPadding,
    zoom: _zoom,
    zoomedXScale,
    zoomedYScale,
    ySelection,
    totalExportLookup,
  }: HighlightLinesProps) => {
    if (!activeItem || !filteredDataLookup.get(activeItem)) return null;

    const item = filteredDataLookup.get(activeItem);

    // Extract the actual data values for accurate positioning
    const distanceValue = item.distance;
    const yValueRaw =
      ySelection === "cog" ? item.cog : totalExportLookup.get(activeItem).pci;

    // Get the positions using the zoomed scales
    const xPosition = zoomedXScale(distanceValue);
    const yPosition = zoomedYScale(yValueRaw);

    return (
      <g style={{ pointerEvents: "none" }}>
        {/* Vertical line from bottom of chart to point */}
        <line
          x1={xPosition}
          y1={height * 2} // Extended beyond bottom of chart
          x2={xPosition}
          y2={yPosition}
          stroke={dataViz.textSecondary}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        {/* Horizontal line from left edge of chart to point */}
        <line
          x1={-width} // Extended beyond left edge
          y1={yPosition}
          x2={xPosition}
          y2={yPosition}
          stroke={dataViz.textSecondary}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  },
);
